import React from "react";
import { Form, Dropdown, Button, Tooltip, OverlayTrigger } from "react-bootstrap";
import { DeleteIcon, PlusIcon } from "../Global/Icons";
import { v4 as uuidv4 } from "uuid";
import { USERS_COLORS } from "../../Utilities/UsersColors";

export type RoleObj = {
  id: string;
  projectId: string;
  roles: string[];
};

type RolesFormProps = {
  isSuperUser: boolean;
  setIsSuperUser: (val: boolean) => void;
  editorIsSuperUser: boolean;
  editorIsProjectManager: boolean;
  groupedRoles: RoleObj[];
  setGroupedRoles: React.Dispatch<React.SetStateAction<RoleObj[]>>;
  newProject: string;
  setNewProject: (val: string) => void;
  newRoleChecks: Record<string, boolean>;
  setNewRoleChecks: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  projectOptions: { label: string; value: string }[];
  loadingProjects: boolean;
  saving: boolean;
  setError: (msg: string | null) => void;
  getProjectName: (id: string) => string | null;
  ROLE_OPTIONS_FOR_SELECT: string[];
};

function getRoleClass(role: string) {
  if (!role) return "role-default";
  const key = role.trim().toLowerCase();
  return (USERS_COLORS as Record<string, string>)[key] ?? "role-default";
}

function joinedTruncate(arr: string[], max = 40) {
  const s = arr.join(", ");
  if (s.length <= max) return s;
  return s.slice(0, max - 2).trimEnd() + "..";
}

export default function RolesForm({
  isSuperUser,
  setIsSuperUser,
  editorIsSuperUser,
  editorIsProjectManager,
  groupedRoles,
  setGroupedRoles,
  newProject,
  setNewProject,
  newRoleChecks,
  setNewRoleChecks,
  projectOptions,
  loadingProjects,
  saving,
  setError,
  getProjectName,
  ROLE_OPTIONS_FOR_SELECT,
}: RolesFormProps) {
  const handleAddRole = () => {
    const selectedRoles = Object.keys(newRoleChecks).filter((k) => newRoleChecks[k]);
    if (selectedRoles.length === 0) {
      setError("Please select at least one role to add.");
      return;
    }

    if (selectedRoles.includes("Project Viewer") && !(newProject ?? "").trim()) {
      setError("Project Viewer requires a project selection. Please choose a project first.");
      return;
    }

    setGroupedRoles((prev) => {
      const pid = newProject ?? "";
      const idx = prev.findIndex((g) => (g.projectId ?? "") === pid);
      if (idx >= 0) {
        const copy = [...prev];
        const existingLower = new Set(copy[idx].roles.map((r) => r.toLowerCase()));
        selectedRoles.forEach((r) => {
          if (!existingLower.has(r.toLowerCase())) {
            copy[idx].roles.push(r);
            existingLower.add(r.toLowerCase());
          }
        });
        return copy;
      } else {
        return [{ id: uuidv4(), projectId: newProject ?? "", roles: selectedRoles }, ...prev];
      }
    });

    setNewProject("");
    const reset: Record<string, boolean> = {};
    ROLE_OPTIONS_FOR_SELECT.forEach((r) => (reset[r] = false));
    setNewRoleChecks(reset);
    setError(null);
  };

  const toggleRoleInGroup = (groupId: string, roleName: string) => {
    setGroupedRoles((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g;
        if (roleName.trim().toLowerCase() === "project viewer" && !(g.projectId ?? "").trim()) {
          setError("Project Viewer requires a project. Assign a project before enabling Project Viewer for this group.");
          return g;
        }
        setError(null);
        return {
          ...g,
          roles: g.roles.includes(roleName)
            ? g.roles.filter((r) => r !== roleName)
            : [...g.roles, roleName],
        };
      })
    );
  };

  const handleRemoveGroup = (groupId: string) => {
    setGroupedRoles((prev) => prev.filter((g) => g.id !== groupId));
  };

  // Check if this group should be disabled for the current editor
  // Returns true if editor cannot manage this group (no access to project)
  const isGroupDisabled = (group: RoleObj): boolean => {
    // Super Users can always manage
    if (editorIsSuperUser) return false;
    
    // If editor is Project Manager and cannot get project name (no access), disable the group
    if (editorIsProjectManager) {
      const projectName = getProjectName(group.projectId);
      // If projectName is null, it means the project is not in the user's accessible projects
      if (projectName === null) {
        return true;
      }
    }
    
    return false;
  };

  // Filter project options to only include those with actual names (not just IDs)
  // A project has a name if the label is different from the value (ID)
  const projectsWithNames = projectOptions.filter(p => {
    // If label exists and is different from the value (ID), it has a real name
    // Also ensure label is not empty and not just the ID repeated
    return p.label && 
           p.label.trim() !== "" && 
           p.label !== p.value &&
           !p.label.includes(p.value); // Extra check to ensure label isn't "ID - ID" format
  });

  return (
    <>
      <h3 className="bold">User Roles</h3>

      <Form.Check
        type="switch"
        style={{ fontWeight: 500 }}
        className="pb-1 orange-switch"
        label="Super User"
        checked={isSuperUser}
        onChange={(e) => setIsSuperUser(e.target.checked)}
        disabled={!editorIsSuperUser}
      />

      <hr />

      {!isSuperUser && (
        <>
          <h4 className="bold mb-2">New Role</h4>
          <div className="mb-4 d-flex align-items-center gap-4">
            <div style={{ minWidth: 150 }}>
              <Form.Select
                aria-label="Select Project"
                className="p-2 bg-light border text-dark text-truncate pe-5"
                style={{ fontWeight: 500 }}
                value={newProject}
                onChange={(e) => {
                  setError(null);
                  setNewProject(e.target.value);
                }}
                disabled={saving || loadingProjects}
              >
                <option value="">No Project</option>
                {projectsWithNames.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div style={{ flex: 1 }}>
              <Dropdown align="start" autoClose="outside">
                <Dropdown.Toggle
                  variant="light"
                  className="d-flex align-items-center gap-2 p-2 pe-5 caret no-caret"
                  style={{ width: "100%", justifyContent: "space-between" }}
                >
                  <div
                    className="text-truncate"
                    style={{ fontSize: "0.9rem", fontWeight: 500, maxWidth: 200 }}
                  >
                    {Object.keys(newRoleChecks).filter((k) => newRoleChecks[k]).length
                      ? joinedTruncate(
                          Object.keys(newRoleChecks).filter((k) => newRoleChecks[k]),
                          60
                        )
                      : "Select Role(s)"}
                  </div>
                </Dropdown.Toggle>

                <Dropdown.Menu style={{ minWidth: 215 }} className="shadow border">
                  <div className="pt-3 px-3">
                    {ROLE_OPTIONS_FOR_SELECT.map((roleOpt) => {
                      const checked = !!newRoleChecks[roleOpt];
                      return (
                        <div
                          key={roleOpt}
                          className="d-flex align-items-center pb-3"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            const updatedChecks = Object.keys(newRoleChecks).reduce(
                              (acc, key) => {
                                acc[key] = key === roleOpt;
                                return acc;
                              },
                              {} as Record<string, boolean>
                            );
                            setNewRoleChecks(updatedChecks);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              const updatedChecks = Object.keys(newRoleChecks).reduce(
                                (acc, key) => {
                                  acc[key] = key === roleOpt;
                                  return acc;
                                },
                                {} as Record<string, boolean>
                              );
                              setNewRoleChecks(updatedChecks);
                            }
                          }}
                          style={{
                            cursor: "pointer",
                            justifyContent: "flex-start",
                            fontWeight: 500,
                          }}
                        >
                          <div className="me-3">
                            <Form.Check
                              type="checkbox"
                              id={`newrole-${roleOpt}`}
                              checked={checked}
                              readOnly
                            />
                          </div>
                          <div>
                            <span
                              className={getRoleClass(roleOpt) + " role"}
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "inline-block",
                                maxWidth: 280,
                              }}
                            >
                              {roleOpt}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Dropdown.Menu>
              </Dropdown>
            </div>

            <div style={{ width: 125, textAlign: "right" }}>
              <Button
                onClick={handleAddRole}
                disabled={Object.keys(newRoleChecks).filter((k) => newRoleChecks[k]).length === 0}
                title="Add role"
                className="p-2"
                style={{ minWidth: 125, padding: 6 }}
              >
                <PlusIcon /> Add Role
              </Button>
            </div>
          </div>

          {groupedRoles.length > 0 && (
            <>
              <hr />
              <div className="mb-4">
                <h4 className="bold mb-2">Edit Roles</h4>
                {groupedRoles.map((g) => {
                  const projectName = getProjectName(g.projectId);
                  const groupDisabled = isGroupDisabled(g);
                  const displayProjectName = projectName ?? "No Projects";
                  
                  return (
                    <div key={g.id} className="mb-4 d-flex align-items-center gap-4">
                      <div
                        style={{
                          minWidth: 180,
                          maxWidth: 100,
                          display: "block",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {displayProjectName}
                      </div>

                      <div style={{ flex: 1 }}>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            groupDisabled ? (
                              <Tooltip>
                                You don't have access to manage roles for this project.
                              </Tooltip>
                            ) : (
                              <Tooltip>Edit roles for this project</Tooltip>
                            )
                          }
                        >
                          <span className="d-block" style={{ width: "100%" }}>
                            <Dropdown align="start" autoClose="outside">
                              <Dropdown.Toggle
                                variant="light"
                                className="d-flex align-items-center gap-2 p-2 no-caret caret"
                                style={{
                                  width: "100%",
                                  justifyContent: "space-between",
                                  fontWeight: 500,
                                }}
                                disabled={saving || groupDisabled}
                              >
                                <div className="text-truncate" style={{ maxWidth: 200 }}>
                                  {g.roles.length ? joinedTruncate(g.roles, 60) : "No roles"}
                                </div>
                              </Dropdown.Toggle>

                              {!groupDisabled && (
                                <Dropdown.Menu style={{ minWidth: 215 }} className="shadow border">
                                  <div className="pt-4 px-4">
                                    {ROLE_OPTIONS_FOR_SELECT.map((roleOpt) => {
                                      const checked = g.roles.some(
                                        (r) => r.trim().toLowerCase() === roleOpt.trim().toLowerCase()
                                      );
                                      return (
                                        <div
                                          key={roleOpt}
                                          className="d-flex align-items-center pb-4"
                                          role="button"
                                          tabIndex={0}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (
                                              roleOpt === "Project Viewer" &&
                                              !(g.projectId ?? "").trim() &&
                                              !checked
                                            ) {
                                              setError(
                                                "Project Viewer requires a project. Assign a project before enabling Project Viewer for this group."
                                              );
                                              return;
                                            }
                                            toggleRoleInGroup(g.id, roleOpt);
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                              e.preventDefault();
                                              if (
                                                roleOpt === "Project Viewer" &&
                                                !(g.projectId ?? "").trim() &&
                                                !checked
                                              ) {
                                                setError(
                                                  "Project Viewer requires a project. Assign a project before enabling Project Viewer for this group."
                                                );
                                                return;
                                              }
                                              toggleRoleInGroup(g.id, roleOpt);
                                            }
                                          }}
                                          style={{
                                            cursor: "pointer",
                                            justifyContent: "flex-start",
                                            fontWeight: 500,
                                          }}
                                        >
                                          <div className="me-3">
                                            <Form.Check
                                              type="checkbox"
                                              id={`edit-${g.id}-${roleOpt}`}
                                              checked={checked}
                                              readOnly
                                            />
                                          </div>
                                          <div>
                                            <span
                                              className={getRoleClass(roleOpt) + " role"}
                                              style={{
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                display: "inline-block",
                                                maxWidth: 280,
                                              }}
                                            >
                                              {roleOpt}
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </Dropdown.Menu>
                              )}
                            </Dropdown>
                          </span>
                        </OverlayTrigger>
                      </div>

                      <div style={{ width: 95 }}>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            groupDisabled ? (
                              <Tooltip>
                                You don't have access to manage roles for this project.
                              </Tooltip>
                            ) : (
                              <Tooltip>Delete role group</Tooltip>
                            )
                          }
                        >
                          <span>
                            <Button
                              variant="danger"
                              onClick={() => handleRemoveGroup(g.id)}
                              disabled={saving || groupDisabled}
                              className="p-2"
                              style={{ minWidth: 95 }}
                            >
                              <DeleteIcon style={{ marginTop: -3, marginRight: 5 }} /> Delete
                            </Button>
                          </span>
                        </OverlayTrigger>
                      </div>
                    </div>
                  );
                })}
              </div>
              <hr />
            </>
          )}
        </>
      )}
    </>
  );
}

